import React, { useCallback, useEffect, useMemo, useState } from "react"
import { CSSTransition } from "react-transition-group"
import i18nData from "@rogue_4/locales/zh-CN.json"
import { StyledComponent } from "@rogue_4/types"
import classNames from "classnames"

import { CommonTransition } from "@/topic/rogue_4/components/CommonTransition"
import { i18n } from "@/utils/i18n"
import { GAME_EQUIP_DICT } from "../../../data/battle"
import GAME_BLUEPRINT_DICT from "../../../data/craft"
import GAME_ITEM_DICT from "../../../data/item"
import { useGameBlueprintUnlock } from "../../../store"
import { SFX } from "../../../utils/sound"
import { getTransitionClassnames } from "../../../utils/transition"
import EquipFrame from "../../EquipFrame"
import { ItemUnit } from "../BackpackModal"
import ModalList from "../common/modalList"
import ModalTitle from "../common/modalTitle"
import { useGameModalStore } from "../store"

import styles from "./index.module.less"

const CraftModal: StyledComponent = ({ className, style }) => {
    const [active, setActive] = useState(false)
    useEffect(() => {
        useGameModalStore.setState({
            callCraftModal: () => {
                setActive(true)
            },
        })
    }, [])
    const handleClose = useCallback(() => {
        SFX.play("commonClick")
        setActive(false)
    }, [])
    const list = useMemo(() => Object.values(GAME_BLUEPRINT_DICT), [])
    const blueprintUnlock = useGameBlueprintUnlock()
    const [activeBlueprint, setActiveBlueprint] = useState(list[0].key)
    const blueprint = useMemo(
        () => GAME_BLUEPRINT_DICT[activeBlueprint],
        [activeBlueprint]
    )
    const unlock = useMemo(
        () => blueprintUnlock[activeBlueprint],
        [activeBlueprint, blueprintUnlock]
    )
    const battleDescriptions = useMemo(
        () =>
            (
                blueprint.description ??
                GAME_EQUIP_DICT[blueprint.result].battleDescription
            ).split("\n"),
        [blueprint]
    )
    const descriptions = useMemo(
        () => GAME_EQUIP_DICT[blueprint.result].description.split("\n"),
        [blueprint]
    )

    return (
        <CSSTransition
            in={active}
            timeout={300}
            classNames={getTransitionClassnames(styles)}
        >
            <div
                className={classNames(styles.craftModal, className)}
                style={style}
                onClick={(e) => {
                    e.stopPropagation()
                }}
            >
                <ModalTitle className={styles.title}>
                    {i18n(i18nData, "rogue_4.game.title.craft")}
                </ModalTitle>
                <CommonTransition state={activeBlueprint}>
                    <div className={styles.cg}>
                        <div className={styles.blueprint}>
                            <ItemUnit
                                className={styles.item}
                                imgClassName={styles.img}
                                item={
                                    unlock
                                        ? blueprint.materials[0]
                                            ? GAME_ITEM_DICT[
                                                  blueprint.materials[0]
                                              ]
                                            : undefined
                                        : undefined
                                }
                                label={unlock ? undefined : "???"}
                            />
                            <div className={styles.plus}></div>
                            <ItemUnit
                                className={styles.item}
                                imgClassName={styles.img}
                                item={
                                    unlock
                                        ? blueprint.materials[1]
                                            ? GAME_ITEM_DICT[
                                                  blueprint.materials[1]
                                              ]
                                            : undefined
                                        : undefined
                                }
                                label={unlock ? undefined : "???"}
                            />
                            <div className={styles.equal}></div>
                            <EquipFrame
                                noType
                                label={unlock ? undefined : "???"}
                                className={styles.equip}
                                equip={
                                    !unlock
                                        ? undefined
                                        : blueprint.result
                                          ? GAME_EQUIP_DICT[blueprint.result]
                                          : undefined
                                }
                            />
                        </div>
                        <div className={styles.battleContent}>
                            {!unlock && (
                                <div className={styles.line}>
                                    <span>
                                        {i18n(
                                            i18nData,
                                            "rogue_4.game.craft.lockNotice"
                                        )}
                                    </span>
                                </div>
                            )}
                            {unlock && battleDescriptions[0] && (
                                <div className={styles.line}>
                                    <span>{battleDescriptions[0]}</span>
                                </div>
                            )}
                            {unlock && battleDescriptions[1] && (
                                <div className={styles.line}>
                                    <span>{battleDescriptions[1]}</span>
                                </div>
                            )}
                            {unlock && (
                                <div
                                    className={classNames(
                                        styles.type,
                                        styles[
                                            GAME_EQUIP_DICT[blueprint.result]
                                                .type
                                        ]
                                    )}
                                ></div>
                            )}
                        </div>
                        <div className={styles.content}>
                            {unlock &&
                                descriptions.map((d, index) => (
                                    <div key={index} className={styles.line}>
                                        <span>{d}</span>
                                    </div>
                                ))}
                        </div>
                    </div>
                </CommonTransition>
                <ModalList
                    className={styles.resultList}
                    list={list.map((b) => ({
                        key: b.key,
                        label: GAME_EQUIP_DICT[b.result].name,
                        disabled: !blueprintUnlock[b.key],
                    }))}
                    cur={activeBlueprint}
                    onChange={setActiveBlueprint}
                />
                <div className={styles.backButton} onClick={handleClose}></div>
            </div>
        </CSSTransition>
    )
}

export default CraftModal
